import React, { useState, useMemo, useCallback } from 'react';
import { i18n } from 'app/utils/i18n';
import { Spinner, Modal, Dropdown } from 'app/components';
import { useEditorSelector, useEditorDispatch } from 'app/state/contexts/EditorContext';
import { DataImage } from 'app/components';
import { Clickable } from 'app/components/Clickable';
import { useImage, useBatchImages } from 'app/api/documents/image-queries';
import { useStyle, useSaveStyle } from 'app/api/style-queries';
import { setDocument as setReduxDocument } from 'app/state/redux/documentSlice';
import { useDelayedSave } from 'app/utils/hooks/delayed-save';
import { useMovePageToSection, useGetSectionPages, useClonePage } from 'app/api/documents/page-queries';
import { Transforms } from 'slate';
import { useSlate } from 'slate-react';
import Tippy from '@tippyjs/react';
import { cloneDeepPage } from 'app/slate/components/page/utils';
import { dispatchMessage, EventType } from 'app/useToasts';
import { usePermissions } from 'app/utils/hooks/document-permission';
import { useDocumentTemplates } from '../../../api/template-queries';
import { customClassExtractor } from 'app/utils/css';
import { Editor } from 'slate';
import { DropdownList } from 'react-widgets';
import { UserContext } from 'app/state/contexts/UserContext';
import { useContext } from 'react';

const HeaderFooterToggle = ({ headerHidden, footerHidden, templateHeaderHidden, templateFooterHidden, onChange }) => {
  // Page-variable overrides template default. If no page-variable, fall back to template.
  const effectiveHeaderHidden = headerHidden !== undefined ? headerHidden : !!templateHeaderHidden;
  const effectiveFooterHidden = footerHidden !== undefined ? footerHidden : !!templateFooterHidden;

  const handleHeaderToggle = () => {
    onChange({ xrpShowHeader: effectiveHeaderHidden ? undefined : 'none' });
  };

  const handleFooterToggle = () => {
    onChange({ xrpShowFooter: effectiveFooterHidden ? undefined : 'none' });
  };

  return (
    <div className="mb-3">
      <div className="text-blue-700 fw-bolder mb-2">{i18n('doc-editor.sidebar.page-settings.xrpShowHeaderFooter')}</div>
      <div className="d-flex flex-row gap-2">
        <label className="d-flex align-items-center gap-2 m-0 cursor-pointer">
          <input type="checkbox" className="form-check-input m-0" checked={!effectiveHeaderHidden} onChange={handleHeaderToggle} />
          <span>{i18n('doc-editor.sidebar.page-settings.xrpShowHeader')}</span>
        </label>
        <label className="d-flex align-items-center gap-2 m-0 cursor-pointer">
          <input type="checkbox" className="form-check-input m-0" checked={!effectiveFooterHidden} onChange={handleFooterToggle} />
          <span>{i18n('doc-editor.sidebar.page-settings.xrpShowFooter')}</span>
        </label>
      </div>
    </div>
  );
};

const BackgroundPicker = ({ name, onChange, defaultValue }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [imageError, setImageError] = useState(false);
  const handleImageError = useCallback(() => setImageError(true), []);
  const handleClose = () => {
    setIsOpen(false);
  };
  const { content } = useEditorSelector((editor) => editor.pDocument);
  const imageSuperId = defaultValue
    ?.match(/\((.*)\)/)
    .pop()
    .split('--xrp-resource-')[1];
  const imageRevisionId = content.images.filter((image) => image.superId === imageSuperId)[0]?.revisionId;
  const { data: fetchedImage } = useImage(imageRevisionId, 'thumbnail');

  const handleSelect = (image) => {
    const newValue = `var(--xrp-resource-${image.file.superId})`;
    setValue(newValue);
    onChange({ [name]: newValue });
    setIsOpen(false);
  };

  const handleDelete = () => {
    setValue();
    onChange({ [name]: undefined });
  };

  const handleClick = () => {
    setIsOpen(true);
  };
  return (
    <div>
      <div className="text-blue-700 fw-bolder mb-1">{i18n('doc-editor.sidebar.page-settings.xrpBackroundImage')}</div>
      <div className="d-flex align-items-center">
        <Clickable className="border flex-grow-1 rounded d-flex align-items-center p-1" onClick={handleClick}>
          {imageError || !fetchedImage ? (
            <div className="bg-blue-300 aspect-1x1 me-2 d-flex align-items-center justify-content-center" style={{ width: '57px' }}>
              <i className="fa-regular fa-image fa-3x" />
            </div>
          ) : (
            <img
              alt=""
              src={`data:${fetchedImage.file?.fileType};base64,${fetchedImage.file?.fileContent}`}
              style={{ width: '57px', objectFit: 'cover' }}
              className="bg-blue-300 aspect-1x1 me-2"
              onError={handleImageError}
            />
          )}
          <div>{value || i18n('doc-editor.sidebar.page-settings.no-image')}</div>
        </Clickable>
        {value ? (
          <button type="button" className={`bg-transparent ms-2 border-0`} onClick={handleDelete}>
            <i className={`fa-regular fa-trash`} />
          </button>
        ) : null}
      </div>
      <BackgroundSelectorModal open={isOpen} onClose={handleClose} onSelect={handleSelect} />
    </div>
  );
};

const SettingsSlider = ({ name, onChange, defaultValue, range, step }) => {
  const [value, setValue] = useState(defaultValue ?? 0);
  const [saveSettings] = useDelayedSave(onChange, 200);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    saveSettings({ [name]: newValue });
  };

  return (
    <div className="mt-4">
      <div className="text-blue-700 fw-bolder">{i18n(`doc-editor.sidebar.page-settings.${name}`)}</div>
      <input
        type="range"
        min={range.at(0)}
        max={range.at(-1)}
        step={step}
        list={`markers-${name}`}
        className="w-100"
        onChange={handleChange}
        name={name}
        value={value}
      />
      <datalist id={`markers-${name}`}>
        {range.map((v) => (
          <option key={v} value={v}></option>
        ))}
      </datalist>
    </div>
  );
};

const MovePage = ({ selectedPage, activeSectionId, onMovePage, documentRevisionId, sections }) => {
  const sectionIds = sections.map((section) => section.revisionId);
  const pDocument = useEditorSelector((editor) => editor.pDocument);
  const [copyPage, setCopyPage] = useState(false);
  const { data, isLoading } = useGetSectionPages(documentRevisionId, sectionIds);

  const { sectionPermissions } = usePermissions(pDocument.superId);
  const formattedPages = data?.flat().map((item) => {
    return {
      sectionId: item.sectionId,
      title: item.pageRevisionId ? `Sida ${item.idx}` : i18n('doc-editor.sidebar.page-settings.move-page.move-to-end'),
      pageId: item.pageRevisionId,
      sectionName: item.name,
      pageIndex: item.internalIdx,
      value: item.id,
      superId: item.superId,
    };
  });

  const fromPage = formattedPages?.filter((page) => page.pageId !== null)[selectedPage - 1];
  const handleChange = (pageId) => {
    const targetPage = formattedPages.find((page) => page.value === pageId);

    onMovePage({ fromPage: fromPage, targetPage, copyPage });
  };
  return (
    <div className="mt-4 ">
      <div>
        <div className="flex flex-row items-center" style={{ marginBottom: '0.5rem' }}>
          <p className="text-blue-700 fw-bolder" style={{ marginBottom: 0 }}>
            {i18n('doc-editor.sidebar.page-settings.move-page.title')}
          </p>
          <Tippy content={<div className="bg-gray-100 p-2 rounded">{i18n('doc-editor.sidebar.page-settings.move-page.desc')}</div>}>
            <i className="fa-light fa-circle-question text-blue-700" style={{ display: 'flex', alignItems: 'center', marginLeft: '0.75rem' }}></i>
          </Tippy>
        </div>
        <div className="flex flex-row items-center mb-2">
          <p className="m-0 me-2">{i18n('doc-editor.sidebar.page-settings.move-page.copy')}</p>
          <input type="checkbox" role="switch" value={copyPage} onChange={(e) => setCopyPage(!copyPage)} />
        </div>
      </div>
      {!isLoading && (
        <Dropdown
          sm
          items={formattedPages.filter((page) => (!copyPage ? page.sectionId !== activeSectionId : true))}
          value={''}
          onChange={handleChange}
          disabled={
            isLoading ||
            formattedPages?.filter((page) => (!copyPage ? page.sectionId !== activeSectionId : true) && sectionPermissions[page.superId] !== 'WRITE')
          }
          groupbyKey={'sectionName'}
          wrapper={({ item }) => {
            return (
              <div className="d-flex align-items-center">
                <span>{item.title}</span>
              </div>
            );
          }}
        />
      )}
    </div>
  );
};

const BackgroundImageItem = ({ imageData, onSelect }) => {
  if (!imageData) {
    return (
      <div className="col-3 text-center">
        <div className="border bg-gray-100 rounded overflow-hidden d-flex align-items-center justify-content-center" style={{ height: '150px' }}>
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="col-3 text-center">
      <Clickable onClick={() => onSelect(imageData)}>
        <div className="border bg-gray-100 rounded overflow-hidden" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <DataImage {...imageData.file} style={{ height: '100%' }} />
          <div className="border-top p-2">{imageData.file.filename}</div>
        </div>
      </Clickable>
    </div>
  );
};

const BackgroundSelectorModalContent = ({ onSelect }) => {
  const pDocument = useEditorSelector((editor) => editor.pDocument);
  const revisionIds = useMemo(() => pDocument.content.images.map((image) => image.revisionId), [pDocument.content.images]);
  const { data: imageDataMap } = useBatchImages(pDocument.superId, revisionIds, 'thumbnail');

  return (
    <div>
      {pDocument.content.images.length === 0 && <h1 className="text-center">{i18n('imageimport.no-images')}</h1>}
      <div className="row">
        {pDocument.content.images.map((image) => (
          <BackgroundImageItem key={image.revisionId} imageData={imageDataMap[image.revisionId]} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
};

const BackgroundSelectorModal = ({ onSelect, open, onClose }) => {
  return (
    <Modal show={open} onClose={onClose} size="lg">
      {open && <BackgroundSelectorModalContent onSelect={onSelect} />}
    </Modal>
  );
};

const ClassPill = ({ className, onRemove }) => (
  <span className="badge bg-blue-100 text-blue-700 d-inline-flex align-items-center me-1 mb-1 px-2 py-1 rounded-pill">
    {className}
    <button
      type="button"
      className="btn-close btn-close-sm ms-1 bg-transparent border-0 p-0"
      onClick={() => onRemove(className)}
      style={{ fontSize: '0.6rem' }}
    >
      <i className="fa-regular fa-xmark" />
    </button>
  </span>
);

const PageClassPicker = ({ editor, pageUuid, docCss, documentPermissions }) => {
  const { user } = useContext(UserContext);
  const lang = user?.userProperties?.cultureId ?? 'sv';
  const { classes: availableClassObjects, error: parseError } = useMemo(() => customClassExtractor(docCss || '', 'page'), [docCss]);
  const availableClasses = useMemo(() => availableClassObjects.map((c) => c.className), [availableClassObjects]);
  const classLabelMap = useMemo(() => {
    const map = {};
    for (const c of availableClassObjects) {
      map[c.className] = lang === 'en' ? c.labelEn : c.labelSv;
    }
    return map;
  }, [availableClassObjects, lang]);

  const currentClasses = useMemo(() => {
    const [pageEntry] = Editor.nodes(editor, {
      at: [],
      match: (n) => n.type === 'page' && n.uuid === pageUuid,
    });
    if (!pageEntry) return [];
    const classStr = pageEntry[0].class || '';
    return classStr
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editor.children triggers recalc on content change
  }, [editor, pageUuid, editor.children]);

  const updateClasses = (newClasses) => {
    const classStr = newClasses.join(', ');
    const [pageEntry] = Editor.nodes(editor, {
      at: [],
      match: (n) => n.type === 'page' && n.uuid === pageUuid,
    });
    if (pageEntry) {
      Transforms.setNodes(editor, { class: classStr || undefined }, { at: pageEntry[1] });
    }
  };

  const handleAdd = (cls) => {
    if (!currentClasses.includes(cls)) {
      updateClasses([...currentClasses, cls]);
    }
  };

  const handleRemove = (cls) => {
    updateClasses(currentClasses.filter((c) => c !== cls));
  };

  const unselectedClasses = useMemo(() => availableClasses.filter((cls) => !currentClasses.includes(cls)), [availableClasses, currentClasses]);

  if (!(documentPermissions.includes('CODER') || documentPermissions.includes('DESIGN'))) return null;
  if (availableClasses.length === 0 && !parseError) return null;

  return (
    <div className="mt-4">
      <div className="text-blue-700 fw-bolder mb-1">{i18n('doc-editor.sidebar.page-settings.page-css-classes')}</div>
      {parseError && (
        <div className="alert alert-warning py-1 px-2 fs-7 mb-2">
          <i className="fa-regular fa-triangle-exclamation me-1" />
          {parseError}
        </div>
      )}
      {availableClasses.length > 0 && (
        <div className="rw-dropdown-wrapper">
          <DropdownList
            data={unselectedClasses.map((cls) => ({ id: cls, value: cls, label: classLabelMap[cls] || cls }))}
            textField="label"
            placeholder={i18n('doc-editor.sidebar.page-settings.select-css-classes')}
            onSelect={(item) => handleAdd(item.value)}
            value={null}
            filter="contains"
            selectIcon={<i className="fa-regular fa-chevron-down" />}
            onChange={() => {}}
          />
        </div>
      )}
      {currentClasses.length > 0 && (
        <div className="d-flex flex-wrap mt-2">
          {currentClasses.map((cls) => (
            <ClassPill key={cls} className={cls} onRemove={handleRemove} />
          ))}
        </div>
      )}
    </div>
  );
};

export const PageVariables = ({ selectedPage, setSelectedPage }) => {
  const editor = useSlate();
  const pDocument = useEditorSelector((editor) => editor.pDocument);
  const activeSection = useEditorSelector((editor) => editor.activeSection);
  const { data: documentTemplates, isLoading: isDocumentTemplatesLoading } = useDocumentTemplates(pDocument.superId);
  const section = useEditorSelector((editor) => editor.section);
  const styleId = pDocument.content.design.find((style) => style.pageId === activeSection.uuid)?.styleId;
  const { data: content } = useStyle({ organization: pDocument.organization, documentRevisionId: pDocument.revisionId, styleId });
  const [localStyle, setLocalStyle] = useState(null);
  const { mutate: saveStyle } = useSaveStyle({
    organization: pDocument.organization,
    documentSuperId: pDocument.superId,
    pageId: activeSection.uuid,
    templateSuperId: activeSection.templateSuperId,
  });

  const { mutate: movePage } = useMovePageToSection({
    onPageRemoved: (fromPage) => {
      Transforms.removeNodes(editor, { at: [fromPage.pageIndex], match: (node) => node.type === 'page' });
    },
  });
  const { mutate: clonePage } = useClonePage({ documentSuperId: pDocument.superId, pageId: activeSection.uuid });
  const { documentPermissions } = usePermissions(pDocument.superId);
  const editorDispatch = useEditorDispatch();
  const style = localStyle ?? (content && 'content' in content ? content.content : content);

  const template = documentTemplates?.find((t) => t.superId === activeSection.templateSuperId);
  const templateStructure = template?.structure;
  const templatePage = Array.isArray(templateStructure) ? templateStructure[0] : templateStructure;
  const templateAreas = templatePage?.areas;

  const onMovePage = (data) => {
    Transforms.select(editor, [0]);
    if (data.fromPage.sectionId === data.targetPage.sectionId && data.copyPage) {
      const pageContent = section.content[data.fromPage.pageIndex];
      const { clonedPage, idMappings } = cloneDeepPage(pageContent);
      clonePage({ pageId: clonedPage.uuid, fromPage: data.fromPage, targetPage: data.targetPage, idMappings });

      Transforms.insertNodes(editor, clonedPage, { at: [data.targetPage.pageIndex] });
    } else {
      if (section.content.length === 1 && !data.copyPage) {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          title: i18n('doc-editor.sidebar.page-settings.move-page.error-title'),
          text: i18n('doc-editor.sidebar.page-settings.move-page.error-desc'),
        });
        return;
      }
      movePage({
        documentId: pDocument.superId,
        fromPage: data.fromPage,
        targetPage: data.targetPage,
        copyPage: data.copyPage,
      });
    }

    setSelectedPage(null);
  };

  async function handleChange(newProperty) {
    const newStyle = style ? Object.assign({}, style) : {};
    if (Object.values(newProperty)[0] === undefined) {
      delete newStyle[Object.keys(newProperty)[0]];
    } else {
      Object.assign(newStyle, newProperty);
    }

    setLocalStyle(newStyle);
    saveStyle(newStyle, {
      onSuccess: (doc) => {
        editorDispatch(setReduxDocument(doc.data));
      },
    });
  }

  if ((style == null && styleId) || isDocumentTemplatesLoading) {
    return <Spinner />;
  }

  return (
    <div className="preview--container">
      <div className="d-flex px-3 pt-4 justify-content-between align-items-center pb-3 w-100 bg-white sticky-top">
        <div className="d-flex align-items-center justify-content-between">
          <button onClick={() => setSelectedPage(null)} className="btn-clear bg-transparent me-3" type="button">
            <i className="fa-regular fa-arrow-left" />
          </button>
          <div className="fw-bold">{i18n('doc-editor.sidebar.page-settings')}</div>
        </div>
        <div className="text-gray-800 fs-7">
          {i18n('doc-editor.sidebar.page-prefix')}
          {selectedPage}
        </div>
      </div>
      <div className="px-3 w-100">
        {activeSection.templateSuperId ? (
          <p>{`${i18n('templates.title')} - ${documentTemplates.find((t) => t.superId === activeSection?.templateSuperId)?.name}`}</p>
        ) : (
          i18n('project-details.project-css.no-template')
        )}
        <HeaderFooterToggle
          headerHidden={'xrpShowHeader' in (style || {}) ? style.xrpShowHeader === 'none' : undefined}
          footerHidden={'xrpShowFooter' in (style || {}) ? style.xrpShowFooter === 'none' : undefined}
          templateHeaderHidden={templateAreas?.header?.hidden}
          templateFooterHidden={templateAreas?.footer?.hidden}
          onChange={handleChange}
        />
        <BackgroundPicker name="xrpBackgroundImage" onChange={handleChange} defaultValue={style?.['xrpBackgroundImage'] ?? null} />
        <PageClassPicker editor={editor} pageUuid={activeSection.uuid} docCss={pDocument.css} documentPermissions={documentPermissions} />
        <SettingsSlider
          name="xrpLineHeightScalingFactor"
          onChange={handleChange}
          defaultValue={style?.['xrpLineHeightScalingFactor'] ?? 0}
          step={0.0005}
          range={[-1.0, -0.5, 0, 0.5, 1]}
        />
        <SettingsSlider
          name="xrpFontSizeScalingFactor"
          onChange={handleChange}
          defaultValue={style?.['xrpFontSizeScalingFactor'] ?? 0}
          step={0.0005}
          range={[-1.0, -0.5, 0, 0.5, 1]}
        />
        <SettingsSlider
          name="xrpLetterSpacingScalingFactor"
          onChange={handleChange}
          defaultValue={style?.['xrpLetterSpacingScalingFactor'] ?? 0}
          step={0.0005}
          range={[-1.0, -0.5, 0, 0.5, 1]}
        />
        <MovePage
          selectedPage={selectedPage}
          documentRevisionId={pDocument.revisionId}
          sections={pDocument.content.sections ?? []}
          activeSectionId={section.revisionId}
          onMovePage={onMovePage}
        />
      </div>
    </div>
  );
};
